// ===================================================================
// VolunteerOS -- Check-In / Check-Out
// Shift code entry, GPS capture, time-gated check-in, elapsed time
// ===================================================================

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Table,
} from '../../components/gab/GabComponents';
import {
  IconLogin, IconLogout, IconMapPin, IconClock, IconBarcode,
  IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import dayjs from 'dayjs';
import {
  useVolunteerStore,
  type VolAssignment,
  type Volunteer,
  type VolEvent,
  mergeExtras,
  parseExtras,
} from '../../store/volunteerStore';
import { semantic } from '../../lib/colors';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── helpers ──────────────────────────────────────────────────────

function fmtTime(iso: string): string {
  if (!iso) return '--';
  return dayjs(iso).format('h:mm A');
}

function elapsedLabel(checkInIso: string): string {
  const mins = dayjs().diff(dayjs(checkInIso), 'minute');
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
}

function hoursWorked(checkInIso: string, checkOutIso: string): string {
  const diff = dayjs(checkOutIso).diff(dayjs(checkInIso), 'minute');
  return (diff / 60).toFixed(2);
}

function isToday(dateStr: string): boolean {
  if (!dateStr) return false;
  return dayjs(dateStr).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
}

function withinCheckInWindow(shiftDate: string, startTime: string): boolean {
  if (!shiftDate) return true; // no date = allow
  const shiftStart = dayjs(
    `${dayjs(shiftDate).format('YYYY-MM-DD')} ${startTime || '00:00'}`,
  );
  const now = dayjs();
  // Allow check-in up to 1 hour before shift start, and any time after
  return now.isAfter(shiftStart.subtract(1, 'hour'));
}

// ── component ────────────────────────────────────────────────────

export default function CheckIn() {
  const { canEdit } = useModulePermission('volunteer');
  const {
    volunteers, events, assignments,
    loadVolunteers, loadEvents, loadAssignments,
    updateAssignment,
  } = useVolunteerStore();

  const [shiftCode, setShiftCode] = useState('');
  const [geoLocation, setGeoLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<number | null>(null);
  const initialLoad = useRef(false);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [, setTick] = useState(0); // force re-render for elapsed time

  // ── initial data load ──────────────────────────────────────────
  useEffect(() => {
    if (initialLoad.current) return;
    initialLoad.current = true;
    loadVolunteers();
    loadEvents();
    loadAssignments();
  }, [loadVolunteers, loadEvents, loadAssignments]);

  // ── tick every 30s for elapsed time display ────────────────────
  useEffect(() => {
    tickRef.current = setInterval(() => setTick(t => t + 1), 30_000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, []);

  // Load settings for GPS enforcement
  const volSettings = useMemo(() => {
    try {
      const raw = localStorage.getItem('vol-org-settings');
      if (raw) return JSON.parse(raw);
    } catch { /* */ }
    return { checkIn: { requireGeo: false, selfCheckin: true } };
  }, []);
  const gpsRequired = volSettings.checkIn?.requireGeo || false;

  // ── GPS ────────────────────────────────────────────────────────
  const acquireGps = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError(gpsRequired ? 'GPS required but not available — check-in blocked' : 'GPS not available — check-in will proceed without location');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoError(null);
      },
      () => setGeoError(gpsRequired ? 'GPS required — enable location services to check in' : 'GPS unavailable — location is optional'),
      { enableHighAccuracy: true, timeout: 5000 },
    );
  }, [gpsRequired]);

  useEffect(() => { acquireGps(); }, [acquireGps]);

  // ── lookup maps ────────────────────────────────────────────────
  const volMap = useMemo(() => {
    const m = new Map<string, Volunteer>();
    volunteers.forEach((v) => m.set(String(v.id), v));
    return m;
  }, [volunteers]);

  const eventMap = useMemo(() => {
    const m = new Map<string, VolEvent>();
    events.forEach((e) => m.set(String(e.id), e));
    return m;
  }, [events]);

  // ── derived lists ──────────────────────────────────────────────
  const readyToCheckIn = useMemo(() => {
    return assignments.filter((a) => {
      if (a.checkIn) return false; // already checked in
      if (a.status !== 'confirmed' && a.status !== 'scheduled') return false;
      return isToday(a.shiftDate);
    });
  }, [assignments]);

  const currentlyCheckedIn = useMemo(() => {
    return assignments.filter((a) => !!a.checkIn && !a.checkOut);
  }, [assignments]);

  const recentActivity = useMemo(() => {
    return assignments
      .filter((a) => a.checkIn && isToday(a.checkIn))
      .sort((a, b) => (b.checkIn || '').localeCompare(a.checkIn || ''));
  }, [assignments]);

  // ── actions ────────────────────────────────────────────────────
  const handleCheckIn = async (a: VolAssignment) => {
    // Block if GPS required but not acquired
    if (gpsRequired && !geoLocation) {
      notifications.show({ title: 'GPS Required', message: 'Enable location services before checking in.', color: 'red' });
      acquireGps();
      return;
    }
    setProcessing(a.id);
    try {
      const updates: Partial<VolAssignment> = {
        checkIn: dayjs().toISOString(),
      };
      if (geoLocation) {
        updates.notes = mergeExtras(a.notes, {
          checkInLat: geoLocation.lat,
          checkInLng: geoLocation.lng,
        });
      }
      await updateAssignment(a.id, updates);
      const vol = volMap.get(String(a.volunteerId));
      const name = vol ? `${vol.firstName} ${vol.lastName}` : `Volunteer #${a.volunteerId}`;
      notifications.show({ message: `${name} checked in`, color: 'green' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Check-in failed';
      notifications.show({ message: msg, color: 'red' });
    } finally {
      setProcessing(null);
    }
  };

  const handleCheckOut = async (a: VolAssignment) => {
    setProcessing(a.id);
    try {
      const now = dayjs().toISOString();
      const hrs = hoursWorked(a.checkIn, now);
      const updates: Partial<VolAssignment> = {
        checkOut: now,
        hours: hrs,
        status: 'completed',
      };
      if (geoLocation) {
        updates.notes = mergeExtras(a.notes, {
          checkOutLat: geoLocation.lat,
          checkOutLng: geoLocation.lng,
        });
      }
      await updateAssignment(a.id, updates);
      const vol = volMap.get(String(a.volunteerId));
      const name = vol ? `${vol.firstName} ${vol.lastName}` : `Volunteer #${a.volunteerId}`;
      notifications.show({ message: `${name} checked out (${hrs} hrs)`, color: 'blue' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Check-out failed';
      notifications.show({ message: msg, color: 'red' });
    } finally {
      setProcessing(null);
    }
  };

  // ── shift-code lookup ──────────────────────────────────────────
  const handleCodeSubmit = () => {
    if (!shiftCode.trim()) {
      notifications.show({ message: 'Enter a shift code', color: 'yellow' });
      return;
    }
    const code = shiftCode.trim().toUpperCase();
    const matchedEvent = events.find(
      (e) => (e.checkInCode || '').toUpperCase() === code,
    );
    if (!matchedEvent) {
      notifications.show({ message: 'No event found for that code', color: 'red' });
      setShiftCode('');
      return;
    }
    // Find assignments for this event that are ready
    const eventAssignments = assignments.filter(
      (a) =>
        String(a.eventId) === String(matchedEvent.id) &&
        !a.checkIn &&
        (a.status === 'confirmed' || a.status === 'scheduled'),
    );
    if (eventAssignments.length === 0) {
      notifications.show({
        message: `Event "${matchedEvent.name}" found but no pending assignments`,
        color: 'yellow',
      });
    } else {
      notifications.show({
        message: `Found ${eventAssignments.length} assignment(s) for "${matchedEvent.name}"`,
        color: 'green',
      });
    }
    setShiftCode('');
  };

  // ── helper to resolve names ────────────────────────────────────
  const volName = (a: VolAssignment): string => {
    const v = volMap.get(String(a.volunteerId));
    if (v) return `${v.firstName} ${v.lastName}`;
    const extras = parseExtras(a.notes);
    if (extras.volunteerName) return String(extras.volunteerName);
    return `Vol #${a.volunteerId}`;
  };

  const eventName = (a: VolAssignment): string => {
    const e = eventMap.get(String(a.eventId));
    return e ? (e.name || e.eventName || `Event #${a.eventId}`) : `Event #${a.eventId}`;
  };

  // ── render ─────────────────────────────────────────────────────
  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Volunteer Check-In</Title>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconRefresh size={14} />}
          onClick={() => { loadAssignments(); loadEvents(); loadVolunteers(); }}
        >
          Refresh
        </Button>
      </Group>

      {/* GPS Status */}
      <Paper p="sm" radius="sm" withBorder mb="md">
        <Group gap="xs">
          <IconMapPin
            size={14}
            style={{ color: geoLocation ? '#07963F' : semantic.error }}
          />
          <Text size="xs" c="dimmed">
            {geoLocation
              ? `GPS: ${geoLocation.lat.toFixed(5)}, ${geoLocation.lng.toFixed(5)}`
              : geoError
                ? `GPS Error: ${geoError}`
                : 'Acquiring GPS...'}
          </Text>
          <Button size="compact-xs" variant="subtle" onClick={acquireGps}>
            Re-acquire
          </Button>
        </Group>
      </Paper>

      {/* Shift Code Entry */}
      <Paper p="md" radius="sm" withBorder mb="lg">
        <Text size="sm" fw={600} mb="xs">
          <IconBarcode size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          Enter Shift Code
        </Text>
        <Group gap="sm">
          <TextInput
            placeholder="e.g. ALPHA1"
            value={shiftCode}
            onChange={(e) => setShiftCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCodeSubmit(); }}
            size="sm"
            style={{ flex: 1, maxWidth: 240 }}
          />
          <Button size="sm" onClick={handleCodeSubmit}>
            Look Up
          </Button>
        </Group>
      </Paper>

      {/* Ready to Check In */}
      <Title order={5} mb="sm">
        <IconLogin size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
        Ready to Check In ({readyToCheckIn.length})
      </Title>
      {readyToCheckIn.length === 0 ? (
        <Paper p="lg" radius="sm" withBorder mb="lg">
          <Text ta="center" c="dimmed" size="sm">
            No assignments ready for check-in today.
          </Text>
        </Paper>
      ) : (
        <Paper p={0} radius="sm" withBorder mb="lg">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Volunteer</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Event</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Shift</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '10px 12px' }} />
                </tr>
              </thead>
              <tbody>
                {readyToCheckIn.map((a) => {
                  const canCheckIn = withinCheckInWindow(a.shiftDate, a.startTime);
                  return (
                    <tr key={a.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 600 }}>{volName(a)}</td>
                      <td style={{ padding: '8px 12px' }}>{eventName(a)}</td>
                      <td style={{ padding: '8px 12px' }}>
                        {a.startTime || '--'} &ndash; {a.endTime || '--'}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <Badge color="blue" size="xs">{a.status}</Badge>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {canEdit && (
                          <Button
                            size="compact-xs"
                            color="green"
                            leftSection={<IconLogin size={14} />}
                            disabled={!canCheckIn}
                            loading={processing === a.id}
                            onClick={() => handleCheckIn(a)}
                            title={canCheckIn ? 'Check in now' : 'Check-in opens 1 hr before shift'}
                          >
                            Check In
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Paper>
      )}

      {/* Currently Checked In */}
      <Title order={5} mb="sm">
        <IconClock size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
        Currently Checked In ({currentlyCheckedIn.length})
      </Title>
      {currentlyCheckedIn.length === 0 ? (
        <Paper p="lg" radius="sm" withBorder mb="lg">
          <Text ta="center" c="dimmed" size="sm">
            No volunteers currently checked in.
          </Text>
        </Paper>
      ) : (
        <Paper p={0} radius="sm" withBorder mb="lg">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Volunteer</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Event</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Checked In</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Elapsed</th>
                  <th style={{ padding: '10px 12px' }} />
                </tr>
              </thead>
              <tbody>
                {currentlyCheckedIn.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{volName(a)}</td>
                    <td style={{ padding: '8px 12px' }}>{eventName(a)}</td>
                    <td style={{ padding: '8px 12px' }}>{fmtTime(a.checkIn)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge color="orange" size="xs">{elapsedLabel(a.checkIn)}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {canEdit && (
                        <Button
                          size="compact-xs"
                          color="blue"
                          leftSection={<IconLogout size={14} />}
                          loading={processing === a.id}
                          onClick={() => handleCheckOut(a)}
                        >
                          Check Out
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}

      {/* Recent Activity */}
      <Title order={5} mb="sm">Recent Activity Today</Title>
      {recentActivity.length === 0 ? (
        <Paper p="lg" radius="sm" withBorder>
          <Text ta="center" c="dimmed" size="sm">No check-in activity today.</Text>
        </Paper>
      ) : (
        <Paper p={0} radius="sm" withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Volunteer</Table.Th>
                <Table.Th>Event</Table.Th>
                <Table.Th>In</Table.Th>
                <Table.Th>Out</Table.Th>
                <Table.Th>Hours</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {recentActivity.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td fw={600}>{volName(a)}</Table.Td>
                  <Table.Td>{eventName(a)}</Table.Td>
                  <Table.Td>{fmtTime(a.checkIn)}</Table.Td>
                  <Table.Td>{a.checkOut ? fmtTime(a.checkOut) : '--'}</Table.Td>
                  <Table.Td>{a.hours || (a.checkOut ? '--' : elapsedLabel(a.checkIn))}</Table.Td>
                  <Table.Td>
                    <Badge
                      size="xs"
                      color={a.checkOut ? 'green' : 'orange'}
                    >
                      {a.checkOut ? 'completed' : 'active'}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </div>
  );
}
